import React, { PureComponent, createRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { store } from 'redux-rest-reducer';
import styled from 'styled-components';
import { Text, Separator, Button } from '@cuemath/comps';
import { post as createSubscriptionPaymentLink } from 'actions/subscription-payment-link';
import { getAll as getAllProducts } from 'actions/subscription-product';
import { getAll as getPlans } from 'actions/subscription-plan';
import { openModal, closeModal } from 'actions/modal';
import { getAll as getTeacherList } from 'actions/teacher-list';
import { TEACHERS_PER_PAGE } from 'constants/list';
import memoize from 'memoize-one';
import {
  Footer,
  Message,
  FormWrapper,
  FieldWrapper,
  ButtonWrapper,
  renderFormField,
  getOptions,
  HeaderHead,
} from 'modules/form';
import {
  FIELD_TYPES,
  subscriptionPaymentFields,
  getOnChangeMethod,
  getInputChangeMethod,
  PAYMENT_TYPE,
  teacherStates,
  ERROR_MSG_FOR_TEACHER_LIST,
  MENU_CLOSE,
  updateFieldChoices,
} from 'constants/form';
import { fetchUniqueOptions } from 'utils';
import { getAll as getAllOnlineBands } from 'actions/online-bands';
import COLORS from 'constants/colors';

const HeaderHeadWrapper = styled(HeaderHead)`
  padding: 10px;
`;

const PRODUCT_TYPE_ID = 'product_type';
const ENROLLMENT_TYPE_ID = 'enrollment_type';
const SUBSCRIPTION_PLAN = 'subscription_plan';
const SUBSCRIPTION_PLANS = 'subscriptionPlans';
const ENROLLMENT_TYPE_ONLINE = 'enrollmentType';
const ENROLLMENT_TYPE_OPTIONS = 'enrollmentTypeOptions';
const SUBSCRIPTION_PRODUCTS = 'subscriptionProducts';
const CURRENCY = 'currency';
const AMOUNT = 'amount';
const AMOUNT_PER_CYCLE = 'amount_per_cycle';
const DURATION = 'duration';
const ONLINE_BAND = 'online_band';

const updateChoices = (newState, fieldName, optionsKey, totalOptions, field) => {
  newState.formFields.find(({ name }) => name === fieldName).choices = getOptions({
    field: field || fieldName,
    [optionsKey]: totalOptions,
  });
  return newState;
};

const enrollmentOptionsAndPlansList = subscriptionPlansData => {
  const enrollmentTypeOptions = [];

  subscriptionPlansData.forEach(({ enrollment_type: enrollmentType }) => {
    if (enrollmentType) {
      enrollmentTypeOptions.push(enrollmentType);
    }
  });

  return fetchUniqueOptions(enrollmentTypeOptions);
};

const CustomLink = styled.a`
  color: ${COLORS.BOSTON_BLUE};
  :visited {
    color: ${COLORS.PURPLE};
  }
  :hover {
    color: blue;
  }
`;

class PaymentModal extends PureComponent {
  static defaultProps = {
    student: null,
  };

  static propTypes = {
    actions: PropTypes.shape({
      createSubscriptionPaymentLink: PropTypes.func.isRequired,
      getAllProducts: PropTypes.func.isRequired,
      getPlans: PropTypes.func.isRequired,
      getTeacherList: PropTypes.func.isRequired,
    }).isRequired,
    student: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      errorText: null,
      message: null,
      form: this.getFormDefaults(),
      formFields: this.getFormFields(),
      subscriptionPaymentLinkPost: store.post(),
      subscriptionPlansList: null,
      subscriptionProductsList: null,
      teachersData: null,
      page: 1,
      teacherSearchKey: null,
      onlinePlanId: null,
      onlineBandOptions: null,
      paymentLink: null,
    };
    subscriptionPaymentFields.forEach(field => {
      const methodName = getOnChangeMethod(field.name);
      this[methodName] = this.onChange.bind(this, field.name);
    });

    this.validationMessageRef = createRef();
  }

  getFormDefaults = () => {
    const formDefaults = {};
    const { student } = this.props;

    subscriptionPaymentFields.forEach(field => {
      formDefaults[field.name] =
        !!student && student[field.param] ? student[field.param] : field.default;
    });
    return formDefaults;
  };

  getFormFields = () => {
    return subscriptionPaymentFields.map(field => {
      if (field.fieldType === FIELD_TYPES.SELECT)
        field.choices = getOptions({
          field: field.name,
        });
      return field;
    });
  };

  static getDerivedStateFromProps(props, state) {
    const newState = {};
    const {
      subscriptionPaymentLink,
      subscriptionPlans,
      subscriptionProducts,
      teacherList,
      onlineBand,
    } = props;
    const {
      subscriptionPaymentLinkPost,
      form,
      formFields,
      page,
      teacherSearchKey,
    } = state;
    newState.form = { ...form };

    const { product_type: productType, online_band } = form || {};
    newState.formFields = formFields;
    const newSubscriptionPaymentLinkPost = store.post(
      subscriptionPaymentLink,
      subscriptionPaymentLinkPost.id,
    );

    const subscriptionProductsData =
      store.getAll(subscriptionProducts) && store.getAll(subscriptionProducts).data;

    const {
      isLoaded: isSubscriptionPlansLoaded,
      data: subscriptionPlansData,
      __ERROR__: error,
    } =
      store.getAll(subscriptionPlans, {
        product_id: productType,
        online_band_id: online_band,
      }) || {};

    if (error) {
      const { message: errorMessage } = error || {};
      newState.errorText = errorMessage;
    }

    const { isLoaded: onlineBandIsLoaded, data: onlineBandData } = store.getAll(
      onlineBand,
    );

    if (onlineBandIsLoaded) {
      newState.onlineBandOptions = onlineBandData;
      const choicesList = getOptions({
        field: ONLINE_BAND,
        onlineBandOptions: onlineBandData,
      });

      newState.formFields.find(field => field.name === ONLINE_BAND).choices = choicesList;
    }

    const { resolved } = newSubscriptionPaymentLinkPost || {};
    const { isCreated, isCreatingFailed, data: { link: paymentLink } = {} } =
      resolved || {};

    if (isCreated) {
      newState.SlotPost = newSubscriptionPaymentLinkPost;
      newState.paymentLink = paymentLink;
      newState.message = 'Subscription Link created successfully.';
      newState.errorText = null;
    } else if (isCreatingFailed) {
      newState.SlotPost = newSubscriptionPaymentLinkPost;
      const errorObj = store.getError(resolved);
      newState.errorText = errorObj ? errorObj.message : 'Something went wrong';
    }
    const listParams = { page, per_page: TEACHERS_PER_PAGE, search: teacherSearchKey };
    newState.teachersData = store.getAll(teacherList, listParams);
    newState.subscriptionProductsList = subscriptionProductsData;
    if (isSubscriptionPlansLoaded && subscriptionPlansData) {
      const enrollmentOptions =
        enrollmentOptionsAndPlansList(subscriptionPlansData) || {};

      updateChoices(
        newState,
        ENROLLMENT_TYPE_ID,
        ENROLLMENT_TYPE_OPTIONS,
        enrollmentOptions,
        ENROLLMENT_TYPE_ONLINE,
      );
    }

    if (newState.subscriptionProductsList.length > 0) {
      updateChoices(
        newState,
        PRODUCT_TYPE_ID,
        SUBSCRIPTION_PRODUCTS,
        subscriptionProductsData,
      );
      updateChoices(newState, SUBSCRIPTION_PLAN, SUBSCRIPTION_PLANS, []);
    }

    if (isSubscriptionPlansLoaded && subscriptionPlansData) {
      updateChoices(
        newState,
        SUBSCRIPTION_PLAN,
        SUBSCRIPTION_PLANS,
        subscriptionPlansData,
      );
      newState.subscriptionPlansList = subscriptionPlansData;
    }
    return newState;
  }

  componentDidUpdate(props, prevState) {
    const {
      form: { product_type: productType },

      errorText,
    } = this.state;

    const { actions } = this.props;
    if (productType !== prevState.form.product_type) {
      actions.getAllOnlineBands();
    }
    if (errorText) {
      this.validationMessageRef.current.scrollIntoView({ block: 'end' });
    }
  }

  componentDidMount() {
    const { actions } = this.props;
    actions.getAllProducts();
  }

  onTeacherSearch = (val, reason) => {
    const { action } = reason;
    const { actions } = this.props;
    const { errorText } = this.state;
    const { EXCLUDE_INACT_INTR } = teacherStates;

    const listParams = { page: 1, per_page: TEACHERS_PER_PAGE, search: val };

    const filterData = {
      page: 1,
      per_page: TEACHERS_PER_PAGE,
      state: EXCLUDE_INACT_INTR,
      search: val,
    };

    if (val && val.length <= 4 && action !== MENU_CLOSE) {
      this.setState({ errorText: ERROR_MSG_FOR_TEACHER_LIST });
      return;
    }
    if (errorText) {
      this.setState({ errorText: null });
    }

    if (action !== MENU_CLOSE || val.length !== 0) {
      this.setState({ teacherSearchKey: val }, () => {
        actions.getTeacherList(listParams, filterData);
      });
    }
  };

  onChange = (key, value) => {
    const { form, errorText } = this.state;
    if (key === 'amount') {
      if (value < 0 || value > 999990) {
        this.setState({
          errorText: 'Please Add Valid Amount',
        });
        return;
      } else if (value.length > 6) return;
    }

    const newForm = { ...form };

    newForm[key] = value;

    const stateChanges = { form: newForm };
    if (errorText) stateChanges.errorText = null;
    this.setState(() => ({ ...stateChanges }));
  };

  onToggle = event => {
    const { form, errorText } = this.state;
    const key = event.target.name;
    const value = event.target.checked;
    const stateChanges = {
      form: {
        ...form,
        [key]: value,
      },
    };
    if (errorText) stateChanges.errorText = null;
    this.setState(() => ({ ...stateChanges }));
  };

  fetchSelectedPlan = (subscriptionPlansList, value) => {
    const selectedPlan = subscriptionPlansList.find(
      ({ gateway_plan_id: gatewayPlanId }) => gatewayPlanId === value,
    );

    return selectedPlan;
  };

  onOptionChange = (key, value) => {
    const { actions } = this.props;
    const { form, errorText, subscriptionPlansList } = this.state;
    const newForm = { ...form };

    newForm[key] = value;

    const updateFieldsValues = (fields, value) => {
      fields.map(field => (newForm[field] = value));
    };

    if (key === PRODUCT_TYPE_ID && form.product_type !== value) {
      updateFieldsValues(
        [ONLINE_BAND, CURRENCY, AMOUNT, AMOUNT_PER_CYCLE, DURATION, ENROLLMENT_TYPE_ID],
        ' ',
      );
      updateFieldsValues([SUBSCRIPTION_PLAN], null);
    }

    if (key === ONLINE_BAND) {
      updateFieldsValues(
        [CURRENCY, AMOUNT, AMOUNT_PER_CYCLE, DURATION, ENROLLMENT_TYPE_ID],
        ' ',
      );
      updateFieldsValues([SUBSCRIPTION_PLAN], null);
      actions.getPlans({
        product_id: newForm.product_type,
        online_band_id: newForm.online_band,
      });
    }

    if (key === SUBSCRIPTION_PLAN) {
      if (subscriptionPlansList.length > 0) {
        const selectedPlan = this.fetchSelectedPlan(subscriptionPlansList, value);
        const {
          currency,
          setup_fee: setupFee,
          amount_per_cycle: amountPerCycle,
          total_cycles: totalCycles,
          enrollment_type: enrollmentType,
        } = selectedPlan || {};

        if (selectedPlan) {
          newForm.currency = currency;
          newForm.amount = setupFee;
          newForm.amount_per_cycle = amountPerCycle;
          newForm.duration = totalCycles;
          newForm.enrollment_type = enrollmentType;
        }
      }
    }

    const stateChanges = { form: newForm };
    if (errorText) stateChanges.errorText = null;
    this.setState(() => ({ ...stateChanges }));
  };

  handlePostAction = () => {
    const { actions, student } = this.props;
    const {
      subscriptionPaymentLinkPost: { id },
      form,
      form: { subscription_plan: subscriptionPlan },
      formFields,
      subscriptionPlansList,
      subscriptionProductsList,
    } = this.state;

    const data = {};

    if (!subscriptionPlan) {
      return this.setState({
        errorText: 'Please select a plan',
      });
    }

    formFields.forEach(({ name, param }) => {
      if (param !== ENROLLMENT_TYPE_ID && param !== ONLINE_BAND) {
        if (name === 'student_id') {
          data['student_id'] = form[name];
        } else data[param] = form[name];
      }
    });
    data['payment_type'] = PAYMENT_TYPE.SUBSCRIPTION;

    if (Array.isArray(subscriptionProductsList)) {
      const product = subscriptionProductsList
        .filter(product => product.id === data[PRODUCT_TYPE_ID])
        .shift();
      if (product) data[PRODUCT_TYPE_ID] = product.code;
    }
    if (Array.isArray(subscriptionPlansList) && subscriptionPlansList.length > 0) {
      const selectedPlan = this.fetchSelectedPlan(
        subscriptionPlansList,
        subscriptionPlan,
      );
      const {
        id,
        setup_fee_duration: setupFeeDuration,
        courses,
        recurring_payment_data: recurringPaymentData,
        total_cycles: totalCycles,
        interval_count: intervalCount,
        interval_unit: intervalUnit,
        total_amount: totalAmount,
      } = selectedPlan || {};
      data['subscription_plan_id'] = id;
      data['duration'] = setupFeeDuration;
      data['courses'] = courses;
      data['recurring_payment_data'] = recurringPaymentData;
      data['teacher_id'] = form['teacher_id'];
      data['subscription_info'] = {
        total_cycles: totalCycles,
        interval_count: intervalCount,
        interval_unit: intervalUnit,
        total_amount: totalAmount,
      };
    }

    actions.createSubscriptionPaymentLink(id, data, { studentId: student.id });
  };

  onSubmit = event => {
    const {
      subscriptionPaymentLinkPost: { current },
    } = this.state;

    event.preventDefault();
    if (!(current && current.isCreating)) this.handlePostAction();
  };

  renderTeachersList = () => {
    const { teachersData, formFields } = this.state;
    const { data } = teachersData || {};

    const teachersDataAvailable = data && Array.isArray(data) && data.length > 0;
    if (teachersDataAvailable) {
      const choicesListOfTeachers = getOptions({
        field: 'teachers_list',
        teachersDataOptions: teachersData.data,
      });

      updateFieldChoices(['teacher_id'], formFields, choicesListOfTeachers);
    }
  };

  renderFormHeader = () => {
    return (
      <HeaderHeadWrapper>
        <Text size="xlarge" align="center">
          Create Subscription Payment Link
        </Text>
      </HeaderHeadWrapper>
    );
  };

  renderFormFields = () => {
    const { formFields, form } = this.state;
    return (
      <FieldWrapper>
        {formFields.map(field => {
          const { disabled: fieldDisabled, editable, label, required, name } =
            field || {};
          const disabled = fieldDisabled || !editable;
          const placeholder = disabled ? '' : label;

          return renderFormField({
            ...field,
            value: form[name],
            onChange: this[getInputChangeMethod(field)],
            onInputChange: this.onTeacherSearch,
            required,
            disabled,
            placeholder,
          });
        })}
      </FieldWrapper>
    );
  };

  renderFormFooter = () => {
    return (
      <Footer>
        <ButtonWrapper flex-direction="row">
          <Button label="Generate" />
        </ButtonWrapper>
      </Footer>
    );
  };

  render() {
    const { errorText, message, form, paymentLink } = this.state;

    return (
      <form onSubmit={this.onSubmit}>
        <FormWrapper>
          {this.renderTeachersList()}
          {this.renderFormHeader()}
          <Separator transparent height="25" />
          <Separator transparent height="25" />
          {this.renderFormFields()}
          {errorText && (
            <Message ref={this.validationMessageRef} error={true}>
              {errorText}
            </Message>
          )}
          <Separator transparent height="30" />
          {message && (
            <Message>
              {message}
              {paymentLink && (
                <CustomLink href={paymentLink} target="_blank" rel="noopener noreferrer">
                  &nbsp;- &nbsp;LINK
                </CustomLink>
              )}
            </Message>
          )}
          <Separator transparent height="25" />
          {this.renderFormFooter()}
          <Separator transparent height="25" />
        </FormWrapper>
      </form>
    );
  }
}

export default connect(
  ({
    subscriptionPaymentLink,
    subscriptionPlans,
    subscriptionProducts,
    teacherList,
    onlineBand,
  }) => ({
    subscriptionPaymentLink,
    subscriptionPlans,
    subscriptionProducts,
    teacherList,
    onlineBand,
  }),
  dispatch => ({
    actions: bindActionCreators(
      {
        createSubscriptionPaymentLink,
        getAllProducts,
        getPlans,
        closeModal,
        openModal,
        getTeacherList,
        getAllOnlineBands,
      },
      dispatch,
    ),
  }),
)(PaymentModal);
